import { transferProcessStartPreCheckResult } from '../constants/enums';
import useAxios from '../hooks/useAxios';

/**
 * Update transfer time record.
 * @param {Object} data - The data object containing the following properties:
 * @param {Object} data.timerRecord - 時間記錄
 * @param {string} data.timerRectod.transId - 移轉單號
 * @param {string} data.timerRecord.startTime - 開始時間
 * @param {string} data.timerRecord.endTime - 結束時間
 * @param {string} data.timerRecord.reasonId - 暫停原因ID
 * @param {string} data.timerRecord.stationType - 站別 枚舉值：stationType
 * @param {string} data.timerRecord.formMachineId - 壓型機台ID
 * @param {string} data.timerRecord.formPowderMaterialBatchNo - 壓型粉料桶號
 * @param {string} data.timerRecord.formPowderMaterialId - 壓型材質ID
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function updateTransTime(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const reqData = {
    elNo: data.elNo,
    formingMachineId: Number(data.machineId),
    formingMachineName: data.machineName,
    isPqcPilot: data.isPQCPilot,
    materialBatchNo: data.bucketNo,
    materialId: Number(data.materialId),
    materialName: data.materialName,
    pilotQuantity: Number(data.pilotTransferQty),
    workId: data.workId,
    sourceTransId: data.transId.substring(2),
    remark: data.remark,
  };
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/v2/pilot/transfer/PRODUCTION',
    reqData,
    true,
    cancelToken
  );

  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Create pilot transfer.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {string} data.materialName - 材質
 * @param {string} data.pilotTransferQty - 試燒數量
 * @param {string} data.machineId - 機台ID
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function createPilotTransfer(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const reqData = {
    elNo: data.elNo,
    formingMachineId: Number(data.machineId),
    formingMachineName: data.machineName,
    isPqcPilot: data.isPQCPilot,
    materialBatchNo: data.bucketNo,
    materialId: Number(data.materialId),
    materialName: data.materialName,
    pilotQuantity: Number(data.pilotTransferQty),
    workId: data.workId,
    sourceTransId: data.transId.substring(2),
    remark: data.remark,
  };
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/v2/pilot/transfer/PRODUCTION',
    reqData,
    true,
    cancelToken
  );

  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get transfer information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getTrans(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/getTrans',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get transfer information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {number} data.formPowderMaterialId - 所選材質
 * @param {string} data.formPowderMaterialBatchNo - 桶號
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function formingStartPreCheck(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/formPilotQcStartPrecheck',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get transfer information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function sinterStartPreCheck(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/sinterPilotQcStartPrecheck',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get timer reason.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.stationType - 站別 枚舉值：stationType
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getTimerReason(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/getTimerReason',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Update grind.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {Object} data.timerRecord - 時間記錄
 * @param {string} data.timerRecord.startTime - 開始時間
 * @param {string} data.timerRecord.endTime - 結束時間
 * @param {string} data.timerRecord.reasonId - 暫停原因ID
 * @param {string} data.timerRecord.stationType - 站別 枚舉值：stationType
 * @param {string} data.grindMachineId - 研磨機台ID
 * @param {string} data.platedMachineId - 通孔機台ID
 * @param {string} data.bothGrindMachineId - 研磨及通孔機台ID
 * @param {string} data.bothPlatedMachineId - 研磨及通孔機台ID
 * @param {string} data.otherMachineId - 其他機台ID
 * @param {string} data.grindQuantity - 研磨數量
 * @param {string} data.platedQuantity - 通孔數量
 * @param {string} data.platedAndGrindQuantity - 研磨及通孔數量
 * @param {string} data.noneQuantity - 其他數量
 * @param {string} data.isMultipleChoice - 是否多選 0: 否 1: 是
 * @param {string} data.isNoneCheck - 是否為其他 0: 否 1: 是
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function updateGrind(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/updateGrind',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}
